import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { signupApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * Admin queue of pending signup requests.
 *
 * Each row links to the magic-link approval page (/approve/:token), reusing
 * the same UI for both the email-driven flow and the dashboard-driven flow.
 *
 * Requests are tagged as either 'homegroup' or 'personal' so admins can
 * see at a glance which kind they're approving.
 */
export default function PendingSignups() {
    const navigate = useNavigate()
    const [requests, setRequests] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        load()
    }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await signupApi.listPending()
            if (response.success) {
                setRequests(response.requests)
            } else {
                toast.error(response.message || 'Could not load requests')
            }
        } catch {
            toast.error('Network error loading requests')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Pending Signups</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        New users awaiting approval. Click Review to approve or reject.
                    </p>
                </div>
                <Button variant="secondary" onClick={load}>Refresh</Button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
                ) : requests.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                        No pending requests. Nice.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {requests.map((r) => {
                            const isPersonal = r.accountType === 'personal'
                            return (
                                <div key={r.magicToken} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                                    {/* Top: name + email + account-type badge */}
                                    <div>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="font-semibold text-gray-900 break-words flex-1">{r.name}</div>
                                            {isPersonal ? (
                                                <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                                    Personal
                                                </span>
                                            ) : (
                                                <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    Homegroup
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 break-all">{r.email}</div>
                                    </div>

                                    {/* Customer info (homegroup only) */}
                                    {!isPersonal && (
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Homegroup</div>
                                            {r.isNewCustomerRequested ? (
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm text-gray-900">{r.requestedCustomerName}</span>
                                                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                        new
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-900">{r.existingCustomerName || '—'}</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Roles (homegroup only) */}
                                    {!isPersonal && (r.litRepName || r.gsrName) && (
                                        <div className="text-xs text-gray-600 space-y-0.5">
                                            {r.litRepName && <div>Lit Rep: <span className="text-gray-900">{r.litRepName}</span></div>}
                                            {r.gsrName && <div>GSR: <span className="text-gray-900">{r.gsrName}</span></div>}
                                        </div>
                                    )}

                                    {/* Personal banner */}
                                    {isPersonal && (
                                        <div className="text-xs text-gray-600">
                                            Will create a personal account.
                                            {(r.city || r.phone) && (
                                                <span className="block mt-1 text-gray-500">
                                                    {[r.city, r.phone].filter(Boolean).join(' · ')}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Submitted date */}
                                    <div className="text-xs text-gray-500">
                                        Submitted {new Date(r.createdAt).toLocaleDateString('en-US', {
                                            month: 'short', day: 'numeric', year: 'numeric',
                                        })}
                                    </div>

                                    {/* Review action */}
                                    <button
                                        onClick={() => navigate(`/approve/${r.magicToken}`)}
                                        className="px-3 py-2 text-sm font-medium text-ghasc-navy bg-gray-50 rounded-lg hover:bg-gray-100 mt-auto"
                                    >
                                        Review →
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}